import { FunctionComponent } from 'react';
import {
  ReservationDateAndTimeDetail,
  ReservationDateAndTimeDetailProps,
} from 'components/business/reservation/ReservationDateAndTimeDetail';
import {
  ReservationDinerAndOwnerDetail,
  ReservationDinerAndOwnerDetailProps,
} from 'components/business/reservation/ReservationDinerAndOwnerDetail';
import {
  ReservationPatronEmailDetail,
  ReservationPatronEmailDetailProps,
} from 'components/business/reservation/ReservationPatronEmailDetail';
import {
  ReservationPriceDetail,
  ReservationPriceDetailProps,
} from 'components/business/reservation/ReservationPriceDetail';
import {
  ReservationQuantityDetail,
  ReservationQuantityDetailProps,
} from 'components/business/reservation/ReservationQuantityDetail';
import {
  ReservationSeatingAreaDetail,
  ReservationSeatingAreaDetailProps,
} from 'components/business/reservation/ReservationSeatingAreaDetail';
import { Theme, css } from '@emotion/react';
import { TypographyProps } from 'omakase';
import { useIsMinRequiredSpendDisplayEnabled } from 'utils/priceDisplayUtils';
import _ from 'lodash';

const rootStyles = ({ spacing }: Theme) => css`
  & li {
    display: flex;
    align-items: center;
    & .MuiTypography-root {
      flex: 1;
      display: inline-block;
      margin-left: ${spacing(1)}px;
    }
    & + li {
      margin-top: ${spacing(1)}px;
    }
  }
`;

type ReservationMetadataListProps = {
  typographyVariant?: TypographyProps['variant'];
  // iconSize?: IconProps['size'];
} & (
  | ({ showDateAndTime: boolean } & ReservationDateAndTimeDetailProps)
  | ({ showQuantity: boolean } & ReservationQuantityDetailProps)
  | ({ showDinerAndOwner: boolean } & ReservationDinerAndOwnerDetailProps)
  | ({ showPriceDetail: boolean } & ReservationPriceDetailProps)
  | ({ showSeatingArea: boolean } & ReservationSeatingAreaDetailProps)
  | ({ showPatronEmail: boolean } & ReservationPatronEmailDetailProps)
);

export const ReservationMetadataList: FunctionComponent<ReservationMetadataListProps> = (props) => {
  const ticketTypeVariety =
    'showPriceDetail' in props && props.showPriceDetail ? props.ticketTypeVariety : undefined;

  const displayPrice = !useIsMinRequiredSpendDisplayEnabled(ticketTypeVariety);

  return (
    <ul css={rootStyles}>
      {'showDateAndTime' in props && props.showDateAndTime && (
        <ReservationDateAndTimeDetail
          {..._.pick(props, [
            'typographyVariant',
            'showPurchaseCount',
            'purchaseCount',
            'showWeekday',
            'displayWeekdayAs',
            'combineDateAndTime',
            'dateTime',
            'isShipping',
            'ticketTypeVariety',
          ])}
        />
      )}
      {'showQuantity' in props && props.showQuantity && (
        <ReservationQuantityDetail
          {..._.pick(props, [
            'typographyVariant',
            'ticketTypeVariety',
            'purchaseCount',
            'purchaseOwner',
            'isAuthenticated',
            'ticketTypeDescriptiveVariety',
            'deliveryServiceProvider',
            'singleUnitQuantity',
          ])}
        />
      )}
      {'showSeatingArea' in props && props.showSeatingArea && props.seatingAreaName && (
        <ReservationSeatingAreaDetail
          {..._.pick(props, ['typographyVariant', 'seatingAreaName'])}
        />
      )}
      {'showDinerAndOwner' in props && props.showDinerAndOwner && (
        <ReservationDinerAndOwnerDetail
          {..._.pick(props, [
            'inVisitEdit',
            'purchaseDiner',
            'purchaseOwner',
            'onSwitchAccounts',
            'typographyVariant',
          ])}
        />
      )}
      {displayPrice && 'showPriceDetail' in props && props.showPriceDetail && (
        <ReservationPriceDetail
          {..._.pick(props, [
            'ticketTypeDefaultPrice',
            'ticketTypeFees',
            'ticketTypeVariety',
            'ticketTypeDescriptiveVariety',
            'typographyVariant',
            'ticketPriceInformation',
          ])}
        />
      )}
      {'showPatronEmail' in props && props.showPatronEmail && props.purchaseOwner?.email && (
        <ReservationPatronEmailDetail {..._.pick(props, ['typographyVariant', 'purchaseOwner'])} />
      )}
    </ul>
  );
};
